import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Plus, GraduationCap, AlertTriangle, AlertCircle, CheckCircle2, Pencil, X, Sparkles, History, ChevronDown, ChevronRight, Download, MinusCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useFetch, Spinner } from '../lib/useFetch';
import { formazioneApi, workersApi, companiesApi } from '../lib/api';
import { useCrudActions } from '../lib/useCrudActions';
import EntityFormModal from '../components/EntityFormModal';
import { formazioneFields } from '../lib/entitySchemas';
import { fmtDate } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import { computeStato, daysUntil } from '../lib/statoUtils';
import { CATEGORIES, labelFor, classifyCorso } from '../lib/formazioneCategories';
import OrphanCleanupButton from '../components/OrphanCleanupButton';
import DuplicatesCleanupButton from '../components/DuplicatesCleanupButton';
import SortAZButton from '../components/SortAZButton';

const STATUS_LABELS = {
  valido: 'Valido', in_scadenza: 'In scadenza', scaduto: 'Scaduto', da_fare: 'Da fare',
};

export const statusBadge = (stato) => {
  const map = {
    valido: { bg: '#dcfce7', color: '#15803d', icon: CheckCircle2, label: 'Valido' },
    in_scadenza: { bg: '#fef7d4', color: '#8a6608', icon: AlertTriangle, label: 'In scadenza' },
    scaduto: { bg: '#fee2e2', color: '#b91c1c', icon: AlertCircle, label: 'Scaduto' },
    da_fare: { bg: '#ede9fe', color: '#6d28d9', icon: Sparkles, label: 'Da fare' },
    consegnato: { bg: '#dcfce7', color: '#15803d', icon: CheckCircle2, label: 'Consegnato' },
    non_richiesto: { bg: '#f1f5f9', color: '#64748b', icon: MinusCircle, label: 'Non richiesto' },
  };
  const s = map[stato] || { bg: '#e2e8f0', color: '#475569', icon: AlertCircle, label: stato || '—' };
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold" style={{ background: s.bg, color: s.color }}>
      <Icon size={12} /> {s.label}
    </span>
  );
};

const KpiCard = ({ label, value, color, bg }) => (
  <div className="rounded-2xl p-5" style={{ background: bg }}>
    <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-600">{label}</div>
    <div className="mt-1 text-3xl font-extrabold" style={{ color }}>{value}</div>
  </div>
);

const DAY_BUCKETS = [30, 60, 90, 120, 150, 180];

const FormazionePage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [params] = useSearchParams();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(params.get('status') || 'all');
  const [companyFilter, setCompanyFilter] = useState(params.get('companyId') || 'all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dueWithin, setDueWithin] = useState('all');
  const [daFareScope, setDaFareScope] = useState('all'); // all|lavoratore|altri
  const [expanded, setExpanded] = useState({});
  const [showPrivati, setShowPrivati] = useState(false);
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    const s = params.get('status');
    const cid = params.get('companyId');
    if (s) setStatusFilter(s);
    if (cid) setCompanyFilter(cid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Reset del filtro azienda quando disattiviamo i privati mentre era selezionato "privati"
  useEffect(() => {
    if (!showPrivati && companyFilter === 'privati') setCompanyFilter('all');
  }, [showPrivati, companyFilter]);

  // Predicate condiviso per la logica "Privati":
  //   - se companyFilter === 'privati' → l'utente vuole vedere SOLO privati
  //     (il toggle showPrivati non si applica, altrimenti non si vedrebbe nulla)
  //   - se companyFilter è una specifica azienda → i privati vengono esclusi
  //   - se companyFilter === 'all' → il toggle showPrivati controlla:
  //     • showPrivati === true  → mostra SOLO i privati
  //     • showPrivati === false → esclude i privati (aziende reali)
  const includePrivatiRecord = React.useCallback((cid) => {
    if (companyFilter === 'privati') return cid === 'privati';
    if (companyFilter !== 'all') return cid !== 'privati';
    return showPrivati ? cid === 'privati' : cid !== 'privati';
  }, [companyFilter, showPrivati]);

  const { data: formazione, loading, reload } = useFetch(() => formazioneApi.list(), []);
  // Includiamo TUTTI i lavoratori (attivi + dismessi + archiviati da merge)
  // così i corsi che fanno riferimento a lavoratori dismessi non mostrano "—"
  // ma il nome reale del lavoratore.
  const { data: workers } = useFetch(() => workersApi.list({ status: 'all' }), []);
  const { data: companies } = useFetch(() => companiesApi.list(), []);
  const { data: daFare } = useFetch(
    () => formazioneApi.daFare({ scope: daFareScope, includePrivati: showPrivati }),
    [daFareScope, showPrivati],
  );
  const crud = useCrudActions({ api: formazioneApi, label: 'corso', onChange: reload });

  const workerName = (id, fallback) => {
    if (fallback) return fallback;
    const w = workers?.find((ww) => ww.id === id);
    return w ? `${w.firstName} ${w.lastName}` : '—';
  };
  const companyName = (id) => companies?.find((c) => c.id === id)?.name || '—';

  // Recompute stato + categoria in JS to guarantee freshness (backend already does the same).
  const enriched = useMemo(() => (formazione || [])
    .filter((f) => includePrivatiRecord(f.companyId))
    .map((f) => {
      const s = computeStato(f.scadenza) || 'valido';
      return { ...f, stato: f.nonRichiesto && s !== 'valido' ? 'non_richiesto' : s };
    })
    .map((f) => ({
      ...f,
      categoria: f.categoria || classifyCorso(f.corso),
    })), [formazione, includePrivatiRecord]);

  // "Corsi senza titolo" = solo quelli che NON hanno né titolo né categoria affidabile.
  // I record dall'Excel v2 non hanno la colonna titolo ma hanno categoria autorevole,
  // quindi NON devono contribuire al banner ambra.
  const emptyCorsoCount = useMemo(
    () => enriched.filter((f) => !f.corso && (!f.categoria || f.categoria === 'altro')).length,
    [enriched],
  );

  // Group renewals by (companyId + workerId + categoria); show most recent, keep the rest as storico.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const f of enriched) {
      const key = `${f.companyId}|${f.workerId}|${f.categoria}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    const out = [];
    for (const [key, items] of map.entries()) {
      items.sort((a, b) => (b.dataRilascio || '').localeCompare(a.dataRilascio || ''));
      out.push({ key, current: items[0], history: items.slice(1) });
    }
    return out;
  }, [enriched]);

  const filteredGroups = useMemo(() => {
    let list = grouped;
    if (statusFilter !== 'all' && statusFilter !== 'da_fare') {
      list = list.filter((g) => g.current.stato === statusFilter);
    }
    if (companyFilter !== 'all') list = list.filter((g) => g.current.companyId === companyFilter);
    if (categoryFilter !== 'all') list = list.filter((g) => g.current.categoria === categoryFilter);
    if (dueWithin !== 'all') {
      const limit = Number(dueWithin);
      list = list.filter((g) => {
        const d = daysUntil(g.current.scadenza);
        return d !== null && d >= 0 && d <= limit;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((g) =>
        (g.current.corso || '').toLowerCase().includes(q)
        || workerName(g.current.workerId, g.current.workerName).toLowerCase().includes(q)
        || companyName(g.current.companyId).toLowerCase().includes(q));
    }
    // Ordinamento alfabetico sul NOME VISIBILE, stile Excel (togglabile A↔Z).
    // Secondario: Categoria per raggruppare i corsi dello stesso lavoratore.
    const workerFor = (wid) => workers?.find((ww) => ww.id === wid);
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...list].sort((a, b) => {
      const wa = workerFor(a.current.workerId);
      const wb = workerFor(b.current.workerId);
      const na = wa ? `${wa.firstName || ''} ${wa.lastName || ''}` : (a.current.workerName || '');
      const nb = wb ? `${wb.firstName || ''} ${wb.lastName || ''}` : (b.current.workerName || '');
      const cmp = String(na).trim().localeCompare(String(nb).trim(), 'it', { sensitivity: 'base' });
      if (cmp !== 0) return cmp * dir;
      const ca = labelFor(a.current.categoria).toLowerCase();
      const cb = labelFor(b.current.categoria).toLowerCase();
      return ca.localeCompare(cb);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped, search, statusFilter, companyFilter, categoryFilter, dueWithin, workers, companies, sortDir]);

  const daFareItems = useMemo(() => {
    if (!daFare?.items) return [];
    let list = daFare.items.filter((d) => includePrivatiRecord(d.companyId));
    if (companyFilter !== 'all' && companyFilter !== 'privati') list = list.filter((d) => d.companyId === companyFilter);
    if (categoryFilter !== 'all') {
      list = list.filter((d) => (d.missing || []).includes(categoryFilter)
        || (d.missing_groups || []).some((mg) => (mg.options || []).some((o) => o.includes(categoryFilter))));
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((d) => (d.workerName || '').toLowerCase().includes(q)
        || companyName(d.companyId).toLowerCase().includes(q));
    }
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daFare, companyFilter, categoryFilter, search, companies, includePrivatiRecord]);

  const categoryCounts = useMemo(() => {
    const out = { all: grouped.length, altro: 0 };
    for (const c of CATEGORIES) out[c.key] = 0;
    for (const g of grouped) {
      const k = g.current.categoria || 'altro';
      out[k] = (out[k] || 0) + 1;
    }
    return out;
  }, [grouped]);

  const visibleCategories = useMemo(() => CATEGORIES.filter((c) => (categoryCounts[c.key] || 0) > 0), [categoryCounts]);

  // Contatori stato: seguono la categoria selezionata (es. HACCP)
  const counts = useMemo(() => {
    const cat = categoryFilter;
    const base = cat === 'all' ? grouped : grouped.filter((g) => (g.current.categoria || 'altro') === cat);
    const daFareN = cat === 'all'
      ? (daFare?.totale || 0)
      : (daFare?.items || []).filter((d) => (d.missing || []).includes(cat)
        || (d.missing_groups || []).some((mg) => (mg.options || []).some((o) => o.includes(cat)))).length;
    return {
      all: base.length,
      valido: base.filter((g) => g.current.stato === 'valido').length,
      in_scadenza: base.filter((g) => g.current.stato === 'in_scadenza').length,
      scaduto: base.filter((g) => g.current.stato === 'scaduto').length,
      non_richiesto: base.filter((g) => g.current.stato === 'non_richiesto').length,
      da_fare: daFareN,
    };
  }, [grouped, daFare, categoryFilter]);

  if (loading) return <Spinner />;
  const showDaFare = statusFilter === 'da_fare';

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard label="Totale (correnti)" value={counts.all} color="#023F80" bg="#e0e7ff" />
        <KpiCard label="Validi" value={counts.valido} color="#15803d" bg="#dcfce7" />
        <KpiCard label="In scadenza" value={counts.in_scadenza} color="#8a6608" bg="#fef7d4" />
        <KpiCard label="Scaduti" value={counts.scaduto} color="#b91c1c" bg="#fee2e2" />
        <KpiCard label="Da fare" value={counts.da_fare} color="#6d28d9" bg="#ede9fe" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#e0e7ff] text-[#023F80] flex items-center justify-center"><GraduationCap size={20} /></div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Formazione lavoratori</h2>
              <p className="text-xs text-slate-500">Una riga per corrente (azienda + lavoratore + categoria). Rinnovi storici disponibili con "Vedi storico".</p>
            </div>
          </div>
          <SortAZButton
            direction={sortDir}
            onToggle={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            testId="formazione-sort-az"
            size="md"
          />
          <button onClick={() => crud.onCreate({ stato: 'valido' })} className="ro-hide px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#E6B800] flex items-center gap-2"><Plus size={14} /> NUOVO CORSO</button>
          <button
            onClick={() => {
              if (showDaFare) {
                // Export della vista "Da fare" con lo scope corrente
                formazioneApi.daFareExport({
                  scope: daFareScope,
                  includePrivati: showPrivati,
                  ...(companyFilter !== 'all' && companyFilter !== 'privati' ? { companyId: companyFilter } : {}),
                });
                return;
              }
              const params = {};
              if (statusFilter !== 'all' && statusFilter !== 'da_fare') params.stato = statusFilter;
              if (categoryFilter !== 'all') params.categoria = categoryFilter;
              if (companyFilter !== 'all') params.companyId = companyFilter;
              params.includePrivati = showPrivati;
              formazioneApi.exportXlsx(params);
            }}
            data-testid="formazione-export-xlsx"
            className="ml-2 px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 flex items-center gap-2"
            title={showDaFare ? 'Esporta la lista Da fare con lo scope selezionato' : 'Esporta i corsi filtrati in Excel'}
          >
            <Download size={13} /> {showDaFare ? `ESPORTA DA FARE (${daFareScope === 'lavoratore' ? 'Solo Lav.' : daFareScope === 'altri' ? 'Altri ruoli' : daFareScope === 'entrambi' ? 'Entrambi' : 'Tutti'})` : 'ESPORTA XLSX'}
          </button>
        </div>

        {emptyCorsoCount > 0 && (
          <div data-testid="formazione-recovery-banner" className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-800 leading-relaxed">
              <strong>{emptyCorsoCount}</strong> corsi senza titolo: aggiornamento dati dal portale di origine in corso.
            </div>
          </div>
        )}

        {(daFare?.totale != null) && (
          <div className="mb-4 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5">
            <div className="text-xs text-slate-600">
              Alcuni corsi risultano <strong>senza titolo</strong> — sono stati spostati in un report dedicato e non contribuiscono alle KPI.
            </div>
            <Link to="/formazione/titoli-da-verificare" data-testid="formazione-open-titoli-da-verificare" className="text-xs font-bold text-[#023F80] hover:underline">
              Apri "Titoli da verificare" →
            </Link>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { key: 'all', label: 'Tutti', n: counts.all },
            { key: 'valido', label: 'Validi', n: counts.valido },
            { key: 'in_scadenza', label: 'In scadenza', n: counts.in_scadenza },
            { key: 'scaduto', label: 'Scaduti', n: counts.scaduto },
            { key: 'da_fare', label: 'Da fare', n: counts.da_fare },
            { key: 'non_richiesto', label: 'Non richiesti', n: counts.non_richiesto },
          ].map((t) => (
            <button key={t.key} data-testid={`formazione-status-${t.key}`} onClick={() => setStatusFilter(t.key)} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${statusFilter === t.key ? 'bg-[#023F80] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{t.label} ({t.n})</button>
          ))}
        </div>

            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 mb-1.5">Categoria del corso</div>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="formazione-category-all"
                  onClick={() => setCategoryFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${categoryFilter === 'all' ? 'bg-[#023F80] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Tutte ({categoryCounts.all || 0})
                </button>
                {visibleCategories.map((c) => (
                  <button
                    key={c.key}
                    data-testid={`formazione-category-${c.key}`}
                    onClick={() => setCategoryFilter(c.key)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${categoryFilter === c.key ? 'bg-[#023F80] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {c.label} ({categoryCounts[c.key] || 0})
                  </button>
                ))}
                {(categoryCounts.altro || 0) > 0 && (
                  <button
                    data-testid="formazione-category-altro"
                    onClick={() => setCategoryFilter('altro')}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${categoryFilter === 'altro' ? 'bg-[#023F80] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    Altro ({categoryCounts.altro})
                  </button>
                )}
              </div>
            </div>

        {!showDaFare && (
          <>
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 mb-1.5">In scadenza entro</div>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="formazione-due-all"
                  onClick={() => setDueWithin('all')}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${dueWithin === 'all' ? 'bg-[#8a6608] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Tutte
                </button>
                {DAY_BUCKETS.map((d) => (
                  <button
                    key={d}
                    data-testid={`formazione-due-${d}`}
                    onClick={() => setDueWithin(d)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${String(dueWithin) === String(d) ? 'bg-[#8a6608] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {d} giorni
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="relative md:col-span-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="formazione-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca corso, lavoratore o azienda..." className="pl-9 h-10 border-gray-200" />
          </div>
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger data-testid="formazione-company-filter" className="h-10 border-gray-200"><SelectValue placeholder="Tutte le aziende" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tutte le aziende</SelectItem>
              {(companies || []).filter(c => c.id && (showPrivati || c.id !== 'privati')).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPrivati((v) => !v)}
              data-testid="formazione-toggle-privati"
              className={`px-4 py-1.5 rounded-full text-xs font-bold ${showPrivati ? 'bg-[#023F80] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              title={showPrivati ? 'Nasconde i corsi dei privati e mostra solo le aziende reali' : 'Mostra SOLO i corsi dei lavoratori privati (senza aziende reali)'}
            >
              {showPrivati ? 'SOLO PRIVATI' : 'MOSTRA PRIVATI'}
            </button>
            <div className="text-xs text-slate-500" data-testid="formazione-results-count">
              {showDaFare ? `${daFareItems.length} da fare` : `${filteredGroups.length} correnti`}
            </div>
          </div>
        </div>

        {!isClient && (
          <div className="mb-3 flex justify-end gap-2">
            <DuplicatesCleanupButton onDone={reload} />
            <OrphanCleanupButton onDone={reload} />
          </div>
        )}
        {!showDaFare && search && daFareItems.length > 0 && (
          <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="formazione-search-dafare-hint">
            <span><b>{daFareItems.length}</b> lavorator{daFareItems.length === 1 ? 'e' : 'i'} trovat{daFareItems.length === 1 ? 'o' : 'i'} con corsi ancora <b>da fare</b> (es. nuovi assunti senza corsi registrati).</span>
            <button onClick={() => setStatusFilter('da_fare')} className="px-3 py-1 rounded-full bg-amber-600 text-white text-xs font-bold hover:bg-amber-700" data-testid="formazione-search-dafare-btn">Mostra in Da fare</button>
          </div>
        )}
        {showDaFare ? (
          <>
            <DaFareTable
              items={daFareItems}
              workerName={workerName}
              companyName={companyName}
              scope={daFareScope}
              onScopeChange={setDaFareScope}
              stats={categoryFilter === 'all' ? (daFare || {}) : {
                totale: daFareItems.length,
                totale_solo_lavoratore: daFareItems.filter((d) => d.lavoratore_gap && !d.altri_gap).length,
                totale_solo_altri: daFareItems.filter((d) => d.altri_gap && !d.lavoratore_gap).length,
                totale_entrambi: daFareItems.filter((d) => d.lavoratore_gap && d.altri_gap).length,
              }}
              sortDir={sortDir}
            />
          </>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3 w-6"></th>
                  <th className="text-left bg-gray-100 px-4 py-3">Categoria / corso</th>
                  <th className="text-left bg-gray-100 px-4 py-3">Lavoratore</th>
                  <th className="text-left bg-gray-100 px-4 py-3">Azienda</th>
                  <th className="text-left bg-gray-100 px-4 py-3">Rilascio più recente</th>
                  <th className="text-left bg-gray-100 px-4 py-3">Scadenza</th>
                  <th className="text-left bg-gray-100 px-4 py-3">Stato</th>
                  <th className="bg-gray-100 rounded-r-xl px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((g) => {
                  const isOpen = !!expanded[g.key];
                  return (
                    <React.Fragment key={g.key}>
                      <tr
                        className={`border-b border-gray-50 hover:bg-gray-50/60 ${g.history.length > 0 ? 'cursor-pointer' : ''}`}
                        data-testid={`formazione-row-${g.current.id}`}
                        onClick={g.history.length > 0 ? () => setExpanded((e) => ({ ...e, [g.key]: !isOpen })) : undefined}
                      >
                        <td className="px-2 py-3 text-center">
                          {g.history.length > 0 ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); setExpanded((es) => ({ ...es, [g.key]: !isOpen })); }}
                              className="p-1 rounded hover:bg-slate-200 text-[#023F80] bg-slate-100"
                              data-testid={`formazione-history-toggle-${g.current.id}`}
                              title={`${g.history.length} rilasci precedenti`}
                            >
                              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          ) : <span className="text-slate-200">·</span>}
                        </td>
                        <td className="px-4 py-3 font-semibold text-gray-800">
                          <div>{labelFor(g.current.categoria)}</div>
                          <div className="text-[11px] text-slate-500 font-normal">
                            {g.current.corso
                              ? g.current.corso
                              : (g.current.source && g.current.source.startsWith('excel-')
                                  ? <span className="text-slate-400 italic">dall'archivio Excel</span>
                                  : <span className="text-amber-700 italic">[Titolo da recuperare]</span>)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{workerName(g.current.workerId, g.current.workerName)}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{companyName(g.current.companyId)}</td>
                        <td className="px-4 py-3 text-gray-700">{fmtDate(g.current.dataRilascio)}</td>
                        <td className="px-4 py-3 text-gray-700">{fmtDate(g.current.scadenza)}</td>
                        <td className="px-4 py-3">{statusBadge(g.current.stato)}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {g.history.length > 0 && (
                              <span className="ro-hide px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold flex items-center gap-1" title="Rinnovi storici" data-testid={`formazione-history-count-${g.current.id}`}>
                                <History size={10} /> {g.history.length}
                              </span>
                            )}
                            <button onClick={() => crud.onEdit(g.current)} title="Modifica" className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><Pencil size={13} className="text-gray-600" /></button>
                            <button onClick={() => crud.onDelete(g.current)} title="Elimina" className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><X size={14} className="text-red-600" /></button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && g.history.map((h) => (
                        <tr key={h.id} className="bg-slate-50/60 text-xs" data-testid={`formazione-history-item-${h.id}`}>
                          <td colSpan={2} className="px-4 py-2 pl-12 text-slate-500">↳ rinnovo precedente: <span className="font-mono text-slate-700">{h.corso}</span></td>
                          <td className="px-4 py-2 text-slate-500">{workerName(h.workerId, h.workerName)}</td>
                          <td className="px-4 py-2 text-slate-400">—</td>
                          <td className="px-4 py-2 text-slate-600">{fmtDate(h.dataRilascio)}</td>
                          <td className="px-4 py-2 text-slate-600">{fmtDate(h.scadenza)}</td>
                          <td className="px-4 py-2">{statusBadge(h.stato)}</td>
                          <td />
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {filteredGroups.length === 0 && <div className="py-10 text-center text-sm text-gray-400">Nessun corso trovato</div>}
          </div>
        )}
      </div>

      <EntityFormModal
        open={crud.open}
        onOpenChange={(v) => { if (!v) crud.closeModal(); }}
        title={crud.editing?.id ? 'Modifica corso' : 'Nuovo corso di formazione'}
        fields={formazioneFields(workers || [], companies || [], isClient)}
        initialValues={crud.editing || {}}
        onSubmit={crud.save}
      />
    </div>
  );
};

const DaFareTable = ({ items, workerName, companyName, scope, onScopeChange, stats, sortDir = 'asc' }) => {
  // Ordinamento alfabetico sul NOME VISIBILE ("cognome nome" come mostrato in tabella), stile Excel.
  const sortedItems = React.useMemo(() => {
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...(items || [])].sort((a, b) => {
      const na = `${a.lastName || ''} ${a.firstName || ''}`.trim();
      const nb = `${b.lastName || ''} ${b.firstName || ''}`.trim();
      return na.localeCompare(nb, 'it', { sensitivity: 'base' }) * dir;
    });
  }, [items, sortDir]);

  const chipCls = (active) => `px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
    active ? 'bg-violet-600 text-white' : 'bg-violet-100 text-violet-800 hover:bg-violet-200'
  }`;

  const totOnlyLav = stats?.totale_solo_lavoratore ?? 0;
  const totOnlyAltri = stats?.totale_solo_altri ?? 0;
  const totEntrambi = stats?.totale_entrambi ?? 0;
  const totAll = stats?.totale ?? sortedItems.length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 mr-1">Filtra per tipo di gap:</span>
        <button
          onClick={() => onScopeChange('all')}
          data-testid="formazione-dafare-scope-all"
          className={chipCls(scope === 'all')}
        >
          Tutti ({totAll})
        </button>
        <button
          onClick={() => onScopeChange('lavoratore')}
          data-testid="formazione-dafare-scope-lavoratore"
          className={chipCls(scope === 'lavoratore')}
          title="Solo Generale/Specifica lavoratori mancanti, nessun corso di ruolo"
        >
          Solo Lavoratori ({totOnlyLav})
        </button>
        <button
          onClick={() => onScopeChange('altri')}
          data-testid="formazione-dafare-scope-altri"
          className={chipCls(scope === 'altri')}
          title="Solo corsi legati ai ruoli mancanti, base+specifica coperti"
        >
          Altri ruoli ({totOnlyAltri})
        </button>
        <button
          onClick={() => onScopeChange('entrambi')}
          data-testid="formazione-dafare-scope-entrambi"
          className={chipCls(scope === 'entrambi')}
          title="Lavoratori con mancanze in ENTRAMBE le categorie"
        >
          Entrambi ({totEntrambi})
        </button>
      </div>
      <p className="text-[10px] text-slate-500 -mt-1 ml-1">
        Totale = Solo Lavoratori + Altri ruoli + Entrambi &nbsp;·&nbsp; <b>{totOnlyLav} + {totOnlyAltri} + {totEntrambi} = {totOnlyLav + totOnlyAltri + totEntrambi}</b>
      </p>

      <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-[11px] text-indigo-900" data-testid="formazione-dafare-info">
        Vista unificata: mostra ogni lavoratore che ha almeno un gap formativo — sia i mancanti <em>Generale/Specifica</em> obbligatori per tutti, sia i corsi legati ai ruoli assegnati. Un corso <strong>scaduto</strong> non copre il requisito.
      </div>

      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-gray-500">
              <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Lavoratore</th>
              <th className="text-left bg-gray-100 px-4 py-3">Azienda</th>
              <th className="text-left bg-gray-100 px-4 py-3">Ruoli assegnati</th>
              <th className="text-left bg-gray-100 px-4 py-3">Corsi mancanti</th>
              <th className="text-left bg-gray-100 rounded-r-xl px-4 py-3">Note</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((w) => (
              <tr key={w.workerId} className="border-b border-gray-50 hover:bg-gray-50/60" data-testid={`formazione-dafare-${w.workerId}`}>
                <td className="px-4 py-3">
                  <Link to={`/lavoratori/${w.workerId}`} className="font-semibold text-[#023F80] hover:underline">
                    {w.lastName} {w.firstName}
                  </Link>
                  <div className="text-[10px] font-mono text-slate-400">{w.fiscalCode || '—'}</div>
                  {w.mansione && <div className="text-[10px] text-slate-500">{w.mansione}{w.sede ? ` · ${w.sede}` : ''}</div>}
                </td>
                <td className="px-4 py-3 text-gray-700 text-xs">
                  {w.companyId ? (
                    <Link to={`/aziende/${w.companyId}`} className="hover:underline">
                      {w.companyName || companyName(w.companyId)}
                    </Link>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(w.roles || []).length === 0 && (
                      <span className="text-[10px] text-slate-400 italic">nessun ruolo</span>
                    )}
                    {(w.roles || []).map((r) => (
                      <span
                        key={r.roleKey}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-800 border border-indigo-100"
                      >
                        {r.roleLabel}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1 mb-1">
                    {w.lavoratore_gap && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-900 border border-violet-200">
                        BASE+SPEC
                      </span>
                    )}
                    {(w.missing || []).map((c) => (
                      <span
                        key={c}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-800 border border-red-200"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                  {(w.missing_groups || []).length > 0 && (
                    <div className="text-[10px] text-red-700 font-mono">
                      {w.missing_groups.map((mg, i) => (
                        <div key={i}>
                          uno tra: {mg.options.map((g) => g.join(' + ')).join(' OR ')}
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {w.warning ? (
                    <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                      {w.warning}
                    </div>
                  ) : (
                    <span className="text-[10px] text-slate-300">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedItems.length === 0 && <div className="py-10 text-center text-sm text-gray-400">Nessun lavoratore con gap formativi</div>}
      </div>
    </div>
  );
};

export default FormazionePage;
